import { ISettings } from '@/shared/interfaces/ISettings';
import { ADMINISTRATION_FEATURES } from '@/utils/administration-features';
import { MAINTENANCE_SETTING_KEYS, normalizeMaintenanceSettingValue } from '@/utils/maintenance-mode';
import { resolveLegacyCustomerSettings } from './legacy-customer-config';
import { publicSettings } from './use-public-settings';
import { effectScope, inject, InjectionKey, reactive, toRef, watch, type Ref } from 'vue';

export const settingsKey: InjectionKey<ISettings> = Symbol('Onify Settings');
export const apiSettings = reactive<ISettings>({});

const BUILT_IN_DEFAULT_SETTINGS: ISettings = {
  // Assign default settings here if not present in `settings.yml`
  _beta_mode: false,
  admin_resources_enabled: true,
  // Every feature stays disabled until the API sends an explicit list, as the API's own default does.
  administration_disabled: [...ADMINISTRATION_FEATURES],
  user_settings: true,
  user_settings_select_language: true,
  user_settings_select_startpage: true,
  user_settings_select_theme: false,
  default_theme: 'legacy',
  custom_title: 'Onify - ',
  default_page_title: 'Helix App',
  max_shown_alerts: 3,
  alerts_location: 'bottom-center',
  workspaces_path: '/workspaces',
  maintenance_mode: false,
  maintenance_mode_bulletin: null,
  maintenance_mode_exclude_roles: ['admin'],
};

export const defaultSettings = reactive<ISettings>({ ...BUILT_IN_DEFAULT_SETTINGS });

/**
 * Settings accessor returned by `useOSettings()`.
 * Members are optional so callers must use `?.value` — keys may arrive after boot.
 */
export type OSettingsRefs = {
  [K in keyof ISettings]?: Ref<ISettings[K]>;
};

/** Memoised toRef wrappers so repeated access to the same key shares one Ref. */
const settingsRefCache = new Map<string, Ref<unknown>>();

function getSettingsRef(key: string): Ref<unknown> {
  let cached = settingsRefCache.get(key);

  if (!cached) {
    // toRef on a missing key becomes live once the key is assigned later.
    cached = toRef(defaultSettings, key as keyof ISettings) as Ref<unknown>;
    settingsRefCache.set(key, cached);
  }

  return cached;
}

/**
 * Hub API client code used to pick the bundled legacy overlay.
 * Authenticated settings win; public bootstrap settings are the pre-auth fallback.
 */
export function resolveActiveAppClientCode(): unknown {
  return apiSettings.app_client_code ?? publicSettings.app_client_code;
}

/** Tenant `app_theme` from authenticated settings, then public bootstrap. */
export function resolveActiveAppTheme(): unknown {
  return apiSettings.app_theme ?? publicSettings.app_theme;
}

/**
 * Hub `default_theme` from authenticated settings, then public bootstrap.
 * Does not use bundled YAML `default_theme` — that is the generic build default.
 */
export function resolveActiveDefaultTheme(): unknown {
  return apiSettings.default_theme ?? publicSettings.default_theme;
}

function buildMergedSettings(localSettings: ISettings, legacyCustomerSettings: Record<string, unknown>): ISettings {
  return {
    ...BUILT_IN_DEFAULT_SETTINGS,
    ...localSettings,
    ...legacyCustomerSettings,
    ...publicSettings,
    ...apiSettings,
  };
}

function applyMergedSettings(merged: ISettings) {
  // Drop keys that are no longer part of the merged snapshot (e.g. legacy-only keys
  // after app_client_code changes to an unbundled customer).
  for (const key of Object.keys(defaultSettings)) {
    if (!Object.hasOwn(merged, key)) {
      delete (defaultSettings as Record<string, unknown>)[key];
    }
  }

  Object.assign(defaultSettings, merged);
}

/**
 * Registers the single watcher that merges apiSettings into defaultSettings.
 *
 * Merge order (lowest to highest precedence):
 *   built-in defaults < localSettings (generic bundled config/settings.yml)
 *   < legacy per-customer settings (BACKWARD COMPATIBILITY ONLY — see
 *     legacy-customer-config.ts) < publicSettings (anonymous GET /public/settings)
 *   < apiSettings (authenticated GET /my/config/settings always wins).
 *
 * Public settings are a Hub API layer, so they sit above the bundled legacy
 * overlay. That is what lets the internal login page pick up `app_theme` and
 * `custom_title` in incognito before any `/my/*` call exists.
 *
 * The legacy per-customer layer is looked up by `resolveActiveAppClientCode()` —
 * authenticated `apiSettings.app_client_code` first, then public bootstrap settings.
 * It is only consulted AFTER the Hub API has told us which client code is active.
 * A client code with no bundled legacy entry (every new, fully API-driven customer)
 * contributes nothing to this merge.
 *
 * Must be called ONCE per application lifecycle, typically from the settings
 * provider plugin (hInstallHelixCore / useProvideHSettings). Using a detached
 * effectScope ensures the watcher is not tied to any component and will not be
 * stopped on component unmount.
 *
 * Returns a stop function so the watcher can be cleaned up when the app is
 * torn down (useful in tests).
 */
function mergeSettingsLayers(localSettings: ISettings) {
  const legacyCustomerSettings = resolveLegacyCustomerSettings(resolveActiveAppClientCode());
  applyMergedSettings(buildMergedSettings(localSettings, legacyCustomerSettings));

  // Maintenance keys must always mirror apiSettings when present — Object.assign
  // alone can leave stale array values on defaultSettings otherwise.
  for (const key of MAINTENANCE_SETTING_KEYS) {
    if (Object.hasOwn(apiSettings, key)) {
      (defaultSettings as Record<string, unknown>)[key] = normalizeMaintenanceSettingValue(
        key,
        (apiSettings as Record<string, unknown>)[key],
      );
    }
  }
}

export function watchApiSettings(localSettings: ISettings): () => void {
  const scope = effectScope(true);
  scope.run(() => {
    watch(
      () => ({
        apiSettings,
        publicSettings,
        appClientCode: resolveActiveAppClientCode(),
      }),
      () => mergeSettingsLayers(localSettings),
      { immediate: true, deep: true },
    );
  });
  return () => scope.stop();
}

/**
 * A Vue composable that provides access to application settings.
 *
 * Returns a Proxy that lazily creates `toRef(defaultSettings, key)` for any key,
 * including keys that do not exist yet. Keys added later (Hub poll, late merge)
 * therefore become reactive without remounting consumers.
 *
 * @returns Settings accessor — use `settings.app_theme?.value` (optional chaining).
 */
export function useOSettings(): OSettingsRefs {
  const localSettings = inject(settingsKey);

  if (!localSettings) {
    throw new Error('useSettings() is called without provider.');
  }

  return new Proxy({} as OSettingsRefs, {
    get(_target, prop) {
      if (typeof prop !== 'string') {
        return undefined;
      }

      return getSettingsRef(prop);
    },
    has(_target, prop) {
      return typeof prop === 'string';
    },
    ownKeys() {
      return Reflect.ownKeys(defaultSettings);
    },
    getOwnPropertyDescriptor(_target, prop) {
      if (typeof prop !== 'string') {
        return undefined;
      }

      return {
        configurable: true,
        enumerable: Object.hasOwn(defaultSettings, prop),
        value: getSettingsRef(prop),
      };
    },
  });
}

/**
 * Clears API settings and restores defaultSettings to built-ins.
 * Used on logout so in-memory state does not outlive the session.
 */
export function resetSettingsRuntimeState(): void {
  for (const key of Object.keys(apiSettings)) {
    delete (apiSettings as Record<string, unknown>)[key];
  }

  for (const key of Object.keys(defaultSettings)) {
    if (!Object.hasOwn(BUILT_IN_DEFAULT_SETTINGS, key)) {
      delete (defaultSettings as Record<string, unknown>)[key];
    }
  }

  Object.assign(defaultSettings, { ...BUILT_IN_DEFAULT_SETTINGS });
  settingsRefCache.clear();
}
